/**
 * Whether an app window should take DOM focus for itself when it becomes the
 * active window (cdb_webos#792).
 *
 * `AppWindow` focuses its root element whenever `isActive` flips to true so
 * keyboard handling (Escape, arrows) lands on the window. When the activation
 * IS a click into a field inside that window, the browser has already focused
 * the field by the time the effect runs — and focusing the window then blurs
 * it: the first click into cdbChat's composer took focus for a moment and lost
 * it, and only the second click typed. It only shows on a window that was not
 * active before the click (another window in front, or a restore from the
 * taskbar), which is why it did not reproduce for everyone.
 *
 * Rule: claim focus only when nothing inside the window has it. Focus on the
 * window itself, on `body`, or anywhere outside the window is fair game.
 */
export function shouldClaimWindowFocus(
  win: HTMLElement | null,
  activeElement: Element | null,
): boolean {
  if (!win) return false
  if (!activeElement || activeElement === win) return true
  return !win.contains(activeElement)
}

/**
 * Whether a focus event should bring its app window to the front (#1794).
 *
 * Only for focus physically inside the window. Events from portaled menus
 * bubble through the React tree too (#324), and a menu item that takes focus
 * just after it opened a new window would push that window behind this one.
 */
export function shouldRaiseWindowOnFocus(win: HTMLElement | null, target: EventTarget | null): boolean {
  return !!win && target instanceof Node && win.contains(target)
}

/**
 * Whether a mousedown inside an app window should move DOM focus to the
 * window root (cdb_webos#1156).
 *
 * Only for clicks physically inside the window — portaled menus bubble
 * through the React tree but live outside it, and stealing their focus kills
 * the selection (#324). And only when nothing inside the window holds focus
 * yet, the #792 rule: an editor that focuses itself DURING mousedown (Monaco
 * does, and prevents the default) would otherwise lose it to the window right
 * away — in Chrome 154 no key reached any JSON editor. An ordinary field is
 * unaffected: the browser focuses it after this handler, as its default.
 */
export function shouldFocusWindowOnMouseDown(
  win: HTMLElement | null,
  target: EventTarget | null,
  activeElement: Element | null,
): boolean {
  if (!win || !(target instanceof Node) || !win.contains(target)) return false
  return shouldClaimWindowFocus(win, activeElement)
}
